"use client";

import { useFeatureFlagEnabled, usePostHog } from "posthog-js/react";
import { useEffect, useRef } from "react";
import { buttonClass } from "@/components/ui/button";
import {
	type AiApplyLane,
	type AiApplyLaneCopy,
	isAiApplyPrimaryAts,
} from "@/lib/apply/ai-apply-lane";
import { useHydrated } from "@/lib/hydration/use-hydrated";
import { lowFitNotice, shouldHideAiApplyBadge } from "@/lib/match/low-fit";
import { useJobContext } from "./job-context";

export {
	AI_APPLY_PRIMARY_ATS,
	isAiApplyPrimaryAts,
} from "@/lib/apply/ai-apply-lane";

export const AI_APPLY_EVERYWHERE_FLAG = "ai-apply-everywhere";

/**
 * Is AI Apply the PRIMARY job-page action for this viewer? Shared by
 * `AiApplyJobButton` (renders it primary) and `JobApplyCta` (demotes the
 * normal button to "Apply manually"), so the two never disagree. Primary only
 * on the `headless` lane (Greenhouse / Lever / Ashby) and not on a low-fit job;
 * every other job still shows AI Apply, as the secondary button.
 *
 * Client-only and post-hydration: the job page is edge-cached and renders the
 * same HTML for everyone, so SSR and the hydration render always get `false`
 * (today's markup) and the swap happens after. `ai-apply-everywhere` is the
 * sole switch: flag off or unresolved → `false` → behaviour unchanged.
 */
export function useAiApplyPrimary(ats: string | null | undefined): boolean {
	const hydrated = useHydrated();
	const enabled = useFeatureFlagEnabled(AI_APPLY_EVERYWHERE_FLAG);
	const { fitScoreBreakdown } = useJobContext();
	const lowFit = shouldHideAiApplyBadge("job_detail", fitScoreBreakdown);
	return hydrated && enabled === true && isAiApplyPrimaryAts(ats) && !lowFit;
}

/** Hunt deep link (#5971) that opens Hunt on this job's approval card, where
 *  Approve starts AI Apply (review sheet or apply pack). Guests go through
 *  sign-in first; a signed-in visitor hitting `/login` is redirected straight
 *  on to the callback, so the guest link is safe for anyone. */
export function aiApplyHref(
	locale: string,
	jobId: string,
	isGuest: boolean,
	primary = false,
): string {
	const hunt = `/${locale}/seeker?job=${encodeURIComponent(jobId)}`;
	// Primary variant: a guest goes straight to Hunt, which serves the anon
	// path logged-out (`hunt-anon`) instead of a login wall; Hunt itself asks
	// for sign-in at the approve step.
	return isGuest && !primary
		? `/${locale}/login?callbackURL=${encodeURIComponent(hunt)}`
		: hunt;
}

/** Telemetry value for the primary button. Kept from the "±2 min" label era
 *  so existing insights keep counting; the label itself no longer has minutes. */
const PRIMARY_VARIANT = "primary_minutes";

/** Props for `ai_apply_cta_shown` / `ai_apply_cta_clicked`. Ids and enums only. */
export function aiApplyEventProps(p: {
	jobId: string;
	ats: string | null;
	lane: AiApplyLane;
	userState: string;
	locale: string;
	lowFit: boolean;
	primary: boolean;
}): Record<string, string | boolean | null> {
	return {
		job_id: p.jobId,
		ats: p.ats,
		lane: p.lane,
		surface: "job_detail",
		user_state: p.userState,
		locale: p.locale,
		low_fit: p.lowFit,
		...(p.primary ? { variant: PRIMARY_VARIANT } : {}),
	};
}

/**
 * AI Apply on the job detail page — on EVERY job (plan A3). The button opens
 * Hunt on this job, where Approve hands the job to the best available lane
 * (`classifyApproveApply`, headless runner, apply pack). The label names the
 * lane honestly (`selectAiApplyLane`, src/lib/apply/ai-apply-lane.ts) so the
 * seeker knows what AI does and what stays with them before the click.
 *
 * Why a deep link and not the review sheet mounted here: `ApplyReviewHost`
 * only has content after `approveHuntApply` has queued the job (consent, caps,
 * tracker row, channel routing), and that flow lives in Hunt. The job page is
 * edge-cached and knows nothing about the viewer at render.
 *
 * Gating: `ai-apply-everywhere` is the sole switch, read after hydration
 * (same #418 reason as `AiApplyBadge`). Never hidden by lane or fit: a
 * low-fit job shows the button with the fit note above it.
 */
export function AiApplyJobButton({
	ats,
	lane,
	copy,
	jobId,
	locale,
}: {
	ats: string | null;
	lane: AiApplyLane;
	copy: AiApplyLaneCopy;
	jobId: string;
	locale: string;
}) {
	const hydrated = useHydrated();
	const enabled = useFeatureFlagEnabled(AI_APPLY_EVERYWHERE_FLAG);
	const posthog = usePostHog();
	const { userState, fitScoreBreakdown } = useJobContext();
	const fitNote = lowFitNotice(locale, fitScoreBreakdown);
	const shownRef = useRef(false);
	const isGuest = userState === "guest";
	const primary = useAiApplyPrimary(ats);

	const visible = hydrated && enabled === true;
	const lowFit = fitNote !== null;

	useEffect(() => {
		if (!visible || shownRef.current) return;
		shownRef.current = true;
		posthog?.capture(
			"ai_apply_cta_shown",
			aiApplyEventProps({
				jobId,
				ats,
				lane,
				userState,
				locale,
				lowFit,
				primary,
			}),
		);
	}, [visible, primary, jobId, ats, lane, userState, locale, lowFit, posthog]);

	if (!visible) return null;

	const fitLine = fitNote ? (
		<p
			data-testid="ai-apply-fit-note"
			className="text-center text-xs font-medium text-muted-foreground"
		>
			{fitNote.headline}
		</p>
	) : null;
	const hint = (
		<p className="text-center text-xs text-muted-foreground">
			{isGuest && !primary ? `${copy.guestPrefix}${copy.hint}` : copy.hint}
		</p>
	);

	return (
		<div
			className={
				primary
					? "flex w-full flex-col gap-1"
					: "flex flex-col items-center gap-1"
			}
		>
			{fitLine}
			<a
				href={aiApplyHref(locale, jobId, isGuest, primary)}
				onClick={() =>
					posthog?.capture(
						"ai_apply_cta_clicked",
						aiApplyEventProps({
							jobId,
							ats,
							lane,
							userState,
							locale,
							lowFit,
							primary,
						}),
					)
				}
				data-testid="ai-apply-job-button"
				data-ats={ats ?? undefined}
				data-lane={lane}
				data-variant={primary ? PRIMARY_VARIANT : undefined}
				className={
					primary
						? buttonClass({ size: "lg", shape: "xl", fullWidth: true })
						: buttonClass({ size: "sm", variant: "secondary" })
				}
			>
				<span className="min-w-0 text-center">{copy.label}</span>
			</a>
			{hint}
		</div>
	);
}
